import {
	CardCartonModel,
	CardCartonRequestModel,
	PacketRequestModel,
	PacketListModel,
	CardPenetrationMetricsResponse,
} from '../../shared/models/card-model';
import { PagedRequest } from '../../shared/models/paged-request.model';
import { AuthenticationService } from './authentication.service';
import { Injectable } from '@angular/core';
import { ApiClientHelper } from '../helpers/api-client.helper';
import { AppConfig } from '../../shared/config/app-config';
import { AggregatorCardModel, Card } from '../../shared/models/card-model';
import { Subject } from 'rxjs';
import { DeviceRegistrationOtpModel } from '../../shared/models/device.model';
import { KycDashboardData } from '../../shared/models/dashboard.model';
import { StringHelper } from '../helpers/strings.helper';
import { LocalStorageService } from './local-storage.service';

@Injectable()
export class CardService {
	private _cardActionModalDestroy = new Subject<any>();
	_cardActionModalDestroy$ = this._cardActionModalDestroy.asObservable();

	private _cardSearchModalDestroy = new Subject<any>();
	_cardSearchModalDestroy$ = this._cardSearchModalDestroy.asObservable();

	private _cardActionSuccessful = new Subject<any>();
	_cardActionSuccessful$ = this._cardActionSuccessful.asObservable();

	cartonRequestSuccess = new Subject<any>();

	private currentCard: Card;
	private qrCode: string;
	private cardLocation: string;
	isSearch: boolean;
	currentCardContext: string;

	private kycDashboardData: KycDashboardData = null;

	constructor(
		private apiClientHelper: ApiClientHelper,
		public config: AppConfig,
		public authService: AuthenticationService,
		public localStorage: LocalStorageService
	) {
	}

	get context() {
		const context = this.authService.getContextUser();
		switch (context) {
			case 'subbrm':
			case 'area-manager':
			case 'aggregators':
				return 'aggregator';
			case 'super-aggregators':
				return 'super-aggregator';
			case 'agents':
				return 'agent';
			default:
				return context;
		}
	}

	subContext(context) {
		let subContext = '';
		switch (context) {
			case 'supreme-aggregator':
				subContext = 'super-aggregator';
				break;
			case 'super-aggregator':
				subContext = 'aggregator';
				break;
		}
		return subContext;
	}

	setCurrentCardContext(context) {
		this.currentCardContext = context;
	}

	getCurrentCardContext() {
		return this.currentCardContext;
	}

	destroySearchModal(reloadPage) {
		if (reloadPage) {
			this.setIsSearch(true);
		} else {
			this.setIsSearch(false);
			this.setCardLocation(null);
			this.setQrcode(null);
		}
		this._cardSearchModalDestroy.next(reloadPage);
	}

	setIsSearch(search) {
		this.isSearch = search;
	}

	getIsSearch() {
		return this.isSearch;
	}

	setCardLocation(location) {
		this.cardLocation = location;
	}

	getCardLocation() {
		return this.cardLocation;
	}

	setQrcode(code) {
		this.qrCode = code;
	}

	getQrcode() {
		return this.qrCode;
	}

	setCurrentCard(card: Card) {
		this.currentCard = card;
	}

	setKycData(kycDashboardData: KycDashboardData) {
		this.kycDashboardData = kycDashboardData;
	}

	getKycData() {
		return this.kycDashboardData;
	}

	getCurrentCard() {
		return this.currentCard;
	}

	setSuccessfulAction(msg: string) {
		this._cardActionSuccessful.next(msg);
	}

	destroyModalCard() {
		this._cardActionModalDestroy.next(true);
	}

	cardsCanUseUserPin() {
		return this.config.CARDS_CAN_USE_USER_PIN;
	}

	canCollectCards() {
		return this.config.CARDS_CAN_COLLECT_CARDS;
	}

	getCards(): Promise<Card[]> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCardSalesSummary(params): Promise<any> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_OFFLINE_SALES_URL}/report/api/v1/cards/brm/sales-summary?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getDataPointSummary(params): Promise<any> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_OFFLINE_SALES_URL}/report/api/v1/cards/brm/business-sold-card-summary?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessesWithSoldCards(params): Promise<any> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_OFFLINE_SALES_URL}/report/api/v1/cards/brm/business-card-sales?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}


	getBOsWithReferrals(params): Promise<any> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_OFFLINE_SALES_URL}/report/api/v1/cards/brm/card-referral-insights?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getPackets(param: PagedRequest, context = this.context): Promise<PacketListModel> {
		const params = StringHelper.toUrlString(param);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/packet-inventory/${context}/paginated?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getPacketRequests(param: PagedRequest, context = this.context): Promise<PacketRequestModel> {
		const params = StringHelper.toUrlString(param);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/packet-inventory/${context}/requests?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	declinePacketRequest(requestId, data, context = this.context): Promise<AggregatorCardModel> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/packet-inventory/${context}/requests/${requestId}/decline`;
		return this.apiClientHelper.postForPromise(url, data);
	}

	approvePacketRequest(requestId, data, context = this.context): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/packet-inventory/${context}/requests/${requestId}/approve`;
		return this.apiClientHelper.postForPromise(url, data);
	}

	collectPacket(ref, context = this.context): Promise<CardCartonModel> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/packet-inventory/${ref}/${context}/collect`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAvailableCartonPacketRefs(context = this.context): Promise<string[]> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/packet-inventory/${context}/available-refs`;
		return this.apiClientHelper.getForPromise(url);
	}

	requestPacket(data): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/packet-inventory/agent/request`;
		return this.apiClientHelper.postForPromise(url, data);
	}

	getAvailableCartonRefs(context = this.context): Promise<string[]> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${context}/available-refs`;
		return this.apiClientHelper.getForPromise(url);
	}

	getOwnCartonRequests(param: PagedRequest, context = this.context): Promise<any> {
		const params = StringHelper.toUrlString(param);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${context}/own-requests?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getOwnPacketRequests(param: PagedRequest, context = this.context): Promise<any> {
		const params = StringHelper.toUrlString(param);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/packet-inventory/${context}/requests-own?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCardCartons(param: PagedRequest, context = this.context): Promise<CardCartonModel> {
		const params = StringHelper.toUrlString(param);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${context}/paginated?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	collectCarton(ref, context = this.context): Promise<CardCartonModel> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${ref}/${context}/collect`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCardRequests(param: PagedRequest, context = this.context): Promise<CardCartonRequestModel> {
		const params = StringHelper.toUrlString(param);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${context}/requests?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	declineCardRequest(requestId, data, context = this.context): Promise<AggregatorCardModel> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${context}/requests/${requestId}/decline`;
		return this.apiClientHelper.postForPromise(url, data);
	}

	approveCardRequest(requestId, data, context = this.context): Promise<AggregatorCardModel> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${context}/requests/${requestId}/approve`;
		return this.apiClientHelper.postForPromise(url, data);
	}

	requestCard(data, context = this.context): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${context}/request`;
		return this.apiClientHelper.postForPromise(url, data);
	}

	getAggregatorCards(urlRequest, param): Promise<AggregatorCardModel> {
		const params = StringHelper.toUrlString(param);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/card-inventory/${urlRequest}/get-all-cards?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	requestForCard(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/request`;
		return this.apiClientHelper.getForPromise(url);
	}

	activateCardOtp(): Promise<DeviceRegistrationOtpModel> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/activate/otp`;
		return this.apiClientHelper.getForPromise(url);
	}

	activateCard(params: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/activate`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	changeCardPin(params: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/pin`;
		return this.apiClientHelper.putForPromiseI(url, params);
	}

	blockCard(params: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/block`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	unBlockCard(params: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/unblock`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	checkIfCardIsAvailable(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/request/validation-data`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCardAgents(): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/agents/by-kyc-verification-status/APPROVED?pageNumber=1&pageSize=1000&keyword=`;
		return this.apiClientHelper.getForPromise(url);
	}

	assignAgent(context, request): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/${context}`;
		return this.apiClientHelper.postForPromise(url, request);
	}

	getAvailableCartons(fulfilmentCenterCode): Promise<any> {
		const url =
			this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/fulfilment-center/${fulfilmentCenterCode}/available-carton-refs`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAvailablePackets(fulfilmentCenterCode, cartonRef): Promise<any> {
		const url =
			this.config.MONIEPOINT_CARDS +
			`/api/v1/carton-inventory/fulfilment-center/${fulfilmentCenterCode}/available-packet-refs/${cartonRef}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAvailableCards(param): Promise<any> {
		const params = StringHelper.toUrlString(param);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/fulfilment-center/get-all-customer-cards?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCardsInPackets(fulfilmentCenterCode, request): Promise<any> {
		const url =
			this.config.MONIEPOINT_CARDS +
			`/api/v1/carton-inventory/fulfilment-center/${fulfilmentCenterCode}/get-all-customer-cards-in-packets`;
		return this.apiClientHelper.postForPromise(url, request);
	}

	getCardInventoryStats(param): Promise<any> {
		const params = StringHelper.toUrlString(param);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/fulfillment-center/card-stats?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	fetchMyFulfillmentCenter(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/fulfillment-center/my-fulfilment-center`;
		return this.apiClientHelper.getForPromise(url);
	}

	getActivatedBusinessCards(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/business-owner`;
		return this.apiClientHelper.getForPromise(url);
	}

	getRequestedBusinessCards(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/${this.config.CARD_TOOLS_UI_UPDATE_V1 ? 'v2' : 'v1'}/cards/delivery-requests`;
		return this.apiClientHelper.getForPromise(url);
	}

	createBusinessCardRequest(param: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/card-request`;
		return this.apiClientHelper.postForPromise(url, param);
	}

	getBusinessUsers(): Promise<any> {
		const url = `${this.config.MONIEPOINT_CARDS}/api/v2/businesses/users`;
		return this.apiClientHelper.getForPromise(url);
	}

	activateBusinessCard(task_id: any, param: any): Promise<any> {
		const url = this.config.CARD_TOOLS_UI_UPDATE_V1 ? (this.config.MONIEPOINT_CARDS + `/api/v3/cards/activate`) : (this.config.MONIEPOINT_CARDS + `/api/v2/cards/${task_id}/activate`);
		return this.apiClientHelper.postForPromise(url, param);
	}

	changeBusinessCardPin(param: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v2/cards/pin`;
		return this.apiClientHelper.putForPromise(url, param);
	}

	changeTransactionLimit(param: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v2/cards/limit`;
		return this.apiClientHelper.putForPromise(url, param);
	}

	getOtp(OTPType: any): Promise<DeviceRegistrationOtpModel> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v2/cards/otp?OTPType=${OTPType}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCardLimit(cardId: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v2/cards/${cardId}/limit`;
		return this.apiClientHelper.getForPromise(url);
	}

	unBlockCardwithOTP(params: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v2/cards/unblock`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	fetchCardDetails(params: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v2/cards/fetch-card-details`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	getCardTransactions(cardId: any, param: any): Promise<any> {
		const params = StringHelper.toUrlString(param);
		const url = this.config.MONIEPOINT_CARDS + `/api/v2/cards/transactions/${cardId}?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	isV1Request(qrCode: string): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v2/cards/is-v1-request?qrCode=${qrCode}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getPushRequest(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/push-request`;
		return this.apiClientHelper.getForPromise(url);
	}

	getPushRequestV2(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/${this.config.CARD_TOOLS_UI_UPDATE_V1 ? 'v2' : 'v1'}/cards/push-requests`;
		return this.apiClientHelper.getForPromise(url);
	}

	acceptPushRequest(params: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/${this.config.CARD_TOOLS_UI_UPDATE_V1 ? 'v3' : 'v1'}/cards/push-request/accept`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	rejectPushRequest(pushedRequestId: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/push-request/reject/${pushedRequestId}`;
		return this.apiClientHelper.putForPromise(url, null);
	}

	getCardSerialExist(serialNumber: string): Promise<{ status: 'ISSUED' | 'NOT_ISSUED' }> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/customer-card/validate?setupKey=${serialNumber}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAggregatorHasCards(aggregatorCode: string): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/customer-card/aggregator/has-cards?aggregatorCode=${aggregatorCode}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getRequestStatistics(context = this.context): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/stats/${context}/request-statistics`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCartonMinimumLevel(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/minimum-carton-level`;
		return this.apiClientHelper.getForPromise(url);
	}

	pushCardToSuperAggregator(superAggregatorId: string): Promise<any> {
		const url =
			this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/supreme-aggregator/request-for-super-aggregator/${superAggregatorId}`;
		return this.apiClientHelper.postForPromise(url, null);
	}

	pushCardToAggregator(aggregatorId: string): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/super-aggregator/request-for-aggregator/${aggregatorId}`;
		return this.apiClientHelper.postForPromise(url, null);
	}

	getCartonStockAndMargin(context = this.context): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${context}/carton-stock/price-margin`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCardStockAndMargin(context = this.context): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${context}/card-stock/price-margin`;
		return this.apiClientHelper.getForPromise(url);
	}

	getSubCartonStock(param: any, context = this.context): Promise<any> {
		const params = StringHelper.toUrlString(param);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${context}/${this.subContext(context)}-stock?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	retrieveCartonsInBulkFromSub(params: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${this.context}/bulk-collect`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	getSubAccountDetailsAndInventoryInfo(code: string, context = this.context): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/${context}/${this.subContext(context)}/${code}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getDownstreamPartnerCartonStock(param: any): Promise<any> {
		let fetcherType = ''
		switch (this.context) {
			case 'supreme-aggregator':
				fetcherType = 'RM';
				break;
			case 'super-aggregator':
				fetcherType = 'SC';
				break;
		}

		const params = StringHelper.toUrlString({ fetcherType, ...param });
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/downstream-partner-info?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getMaxCartonPush(id: any, context = this.context): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${context}/${this.subContext(context)}/${id}/max-carton-push`;
		return this.apiClientHelper.getForPromise(url);
	}

	getPricePerCarton(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/price-per-carton`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCustomerPricePerCard(queryParams): Promise<any> {
		const queryString = StringHelper.toUrlString(queryParams);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/cost-margin?${queryString}`;
		return this.apiClientHelper.getForPromise(url);
	}
	getCardCost(queryParams): Promise<any> {
		const queryString = StringHelper.toUrlString(queryParams);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/cost?${queryString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCustomerCardCostConfig(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v2/config/debit-card/cost`;
		return this.apiClientHelper.getForPromise(url);
	}

	requestAndApprove(superAggregatorId: string, params: any, context = this.context): Promise<any> {
		const url =
			this.config.MONIEPOINT_CARDS +
			`/api/v1/carton-inventory/${context}/request-and-approve/${this.subContext(context)}/${superAggregatorId}`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	getMyRM(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/aggregators/sc/regional-manager`;
		return this.apiClientHelper.getForPromise(url);
	}

	getMySC(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/aggregators/brm/state-coordinator`;
		return this.apiClientHelper.getForPromise(url);
	}

	searchAccountNumber(acc: string): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/account/validate?accountNumber=${acc}`;
		return this.apiClientHelper.getForPromise(url);
	}

	searchSalesAgentAccountNumber(accountNumber: string): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/sales-agents/account-number/${accountNumber}`;
		return this.apiClientHelper.getForPromise(url);
	}

	validateOTP(otp: string): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/otp/validate?otp=${otp}`;
		return this.apiClientHelper.getForPromise(url);
	}

	resetPin(params: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v2/cards/reset-pin`;
		return this.apiClientHelper.putForPromise(url, params);
	}

	getDisputeDetails(disputeId: string): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/disputes/public/${disputeId}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCardOwnershipStatus(): Promise<{ hasPendingCardPush: string; hasCardRequest: string }> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/business-owners/cards/requests/status`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCardVisibilityByBoStateId(stateId: string | number): Promise<{ cardVisibleToState: boolean }> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v2/cards/menu-visibility/state/${stateId}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCardPenetrationMetrics(): Promise<CardPenetrationMetricsResponse> {
		const url = this.config.MONIEPOINT_REPORT + `/api/v1/cards/metrics`;
		return this.apiClientHelper.getForPromise(url);
	}

	getDeliveryAreas(lgaCode: string): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/delivery-areas?lgaCode=${lgaCode}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getSalesAgentCardInventoryInfo(accountNumber: string): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/brm/agent/nuban/${accountNumber}/card-inventory-info`;
		return this.apiClientHelper.getForPromise(url);
	}

	getSalesAgentAccountAndInventoryInfo(code: string): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/aggregator/marketer/${encodeURIComponent(code)}`;
		return this.apiClientHelper.getForPromise(url);
	}

	retrieveCardsFromSalesAgent(params: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/card-retrieval-batch/collect`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	canUseDeliveryService() {
		return this.config.CAN_USE_DELIVERY_SERVICE;
	}

	canUseDeviceId() {
		return this.config.USE_CARD_DEVICE_ID;
	}

	getBRMCartonRequestsToSC(queryParams): Promise<any> {
		const queryString = StringHelper.toUrlString(queryParams);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/aggregators/requests-history/state-coordinator?${queryString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBrmUnassignedCards(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/brm/cards/count/unassigned`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBoPerformance(): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/brm/sales-agents/performance`;
		return this.apiClientHelper.getForPromise(url);
	}

	getCustomerFlexibleCardCosts(queryParams): Promise<any> {
		const queryString = StringHelper.toUrlString(queryParams);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/card-push-requests/customer-cost?${queryString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	acceptTermsAndConditions(params: any): Promise<any> {
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/card-tools/activity-logging/activate/terms-conditions/accept`;
		return this.apiClientHelper.postForPromise(url, params);
	}


	getCardsDocument(documentKey, queryParams): Promise<any> {
		const queryString = StringHelper.toUrlString(queryParams);
		const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/documents/keys/${documentKey}?${queryString}`;
		return this.apiClientHelper.getForPromise(url);
	}
}
